import Vue from 'vue';
import { v4 as uuid } from 'uuid';

export default {
  // ============================================
  // Record mutations (API-backed page documents)
  // ============================================

  // Set a single record from API response.
  // Payloads { record, teamId } drop only when the team
  // differs — a same-team catalog refetch may bump catalogToken
  // without invalidating an in-flight page fetch.
  setRecord: (state, payload) => {
    const record = payload?.record;
    if (!record?.id) return;

    if (isStaleTeamRecordWrite(state, payload)) return;

    Vue.set(state.recordsById, record.id, record);
  },

  // Set multiple records (e.g., from list endpoint).
  // Payloads { records, teamId, token } belong to one catalog
  // request: ignore a stale team/token, replace when the team differs.
  setRecords: (state, { records, teamId, token }) => {
    if (token !== state.catalogToken) return;
    if (
      state.pendingCatalogTeamId != null &&
      teamId !== state.pendingCatalogTeamId
    ) {
      return;
    }
    if (teamId !== state.catalogTeamId) {
      emptyCatalog(state, teamId);
    }

    (records || []).forEach((record) => {
      Vue.set(state.recordsById, record.id, record);
    });

    state.treeLoaded = true;
    state.catalogTeamId = teamId ?? state.catalogTeamId;
  },

  // Update a record (partial update)
  updateRecord: (state, { recordId, data }) => {
    const record = state.recordsById[recordId];
    if (!record) return;
    Vue.set(state.recordsById, recordId, { ...record, ...data });
  },

  // Remove a record
  removeRecord: (state, { recordId }) => {
    Vue.delete(state.recordsById, recordId);
  },

  // ============================================
  // Page lifecycle (in-memory page content)
  // ============================================

  createPage: (
    state,
    { pageId, meta = {}, recordId = null, streamShell = false }
  ) => {
    Vue.set(state.pagesById, pageId, {
      id: pageId,
      recordId, // Links to API record, null for local-only pages
      streamShell: !!streamShell,
      created_at: Date.now(),
      updated_at: Date.now(),
      meta,
    });
    // Initialize root children array
    Vue.set(state.childrenByParent, `root_${pageId}`, []);
  },

  // Update page's recordId (after first save to API)
  setPageRecordId: (state, { pageId, recordId }) => {
    const page = state.pagesById[pageId];
    if (page) {
      page.recordId = recordId;
    }
  },

  // A stream shell is not a hydrated Mongo page. Clear the flag and
  // attach record metadata so loadRecordPage can fill persisted nodes.
  hydrateStreamPage: (state, { pageId, recordId, meta = {} }) => {
    const page = state.pagesById[pageId];
    if (!page) return;
    page.streamShell = false;
    if (recordId) page.recordId = recordId;
    page.meta = { ...page.meta, ...meta };
    page.updated_at = Date.now();
  },

  deletePage: (state, { pageId }) => {
    // Remove all nodes belonging to this page
    Object.keys(state.nodesById).forEach((nodeId) => {
      if (state.nodesById[nodeId].pageId === pageId) {
        Vue.delete(state.nodesById, nodeId);
      }
    });
    // Clean up childrenByParent entries
    Object.keys(state.childrenByParent).forEach((key) => {
      if (key.endsWith(pageId) || state.nodesById[key]?.pageId === pageId) {
        Vue.delete(state.childrenByParent, key);
      }
    });
    Vue.delete(state.pagesById, pageId);
  },

  setActivePage: (state, pageId) => {
    state.activePageId = pageId;
  },

  // Node operations - localized reactivity
  insertNode: (state, { node }) => {
    const nodeId = node.id || uuid();
    const parentKey = node.parentId || `root_${node.pageId}`;

    const droppableTypes = ['layout', 'list'];
    const defaultDroppable = droppableTypes.includes(node.nodeType);

    Vue.set(state.nodesById, nodeId, {
      ...node,
      id: nodeId,
      layout: node.layout || null,
      height: node.height || null,
      draggable: node.draggable !== false,
      droppable: node.droppable ?? defaultDroppable,
    });

    // Initialize children array for this node
    if (!state.childrenByParent[nodeId]) {
      Vue.set(state.childrenByParent, nodeId, []);
    }

    // Add to parent's children array (in-place mutation for stability)
    const siblings = state.childrenByParent[parentKey] || [];
    if (node.afterId) {
      const afterIndex = siblings.indexOf(node.afterId);
      siblings.splice(afterIndex + 1, 0, nodeId);
    } else {
      siblings.push(nodeId);
    }
    Vue.set(state.childrenByParent, parentKey, siblings);
  },

  patchNodeProps: (state, { nodeId, props }) => {
    const node = state.nodesById[nodeId];
    if (!node || !props) return;
    if (!node.props) Vue.set(node, 'props', {});
    Object.keys(props).forEach((key) => {
      Vue.set(node.props, key, props[key]);
    });
    node.updated_at = Date.now();
  },

  patchNodeLayout: (state, { nodeId, layout, replace = false }) => {
    const node = state.nodesById[nodeId];
    if (!node) return;
    if (layout === null) {
      Vue.set(node, 'layout', null);
    } else {
      Vue.set(node, 'layout', {
        ...(replace ? {} : node.layout || {}),
        ...layout,
      });
    }
    node.updated_at = Date.now();
  },

  patchNodeHeight: (state, { nodeId, height }) => {
    const node = state.nodesById[nodeId];
    if (!node || !height) return;
    Vue.set(node, 'height', {
      ...(node.height || {}),
      ...height,
    });
    node.updated_at = Date.now();
  },

  appendChunk: (state, { nodeId, text }) => {
    const node = state.nodesById[nodeId];
    if (!node || !node.props.chunks) return;
    node.props.chunks.push(text);
  },

  moveNode: (state, { nodeId, newParentId, afterId }) => {
    const node = state.nodesById[nodeId];
    if (!node) return;

    const oldParentKey = node.parentId || `root_${node.pageId}`;
    const newParentKey = newParentId || `root_${node.pageId}`;

    // Remove from old parent
    const oldSiblings = state.childrenByParent[oldParentKey];
    const oldIndex = oldSiblings.indexOf(nodeId);
    if (oldIndex > -1) oldSiblings.splice(oldIndex, 1);

    // Add to new parent. Explicit null = insert before the first sibling
    // (drop above/left of the first block). Omitted/undefined still appends.
    const newSiblings = state.childrenByParent[newParentKey] || [];
    if (afterId) {
      const afterIndex = newSiblings.indexOf(afterId);
      newSiblings.splice(afterIndex + 1, 0, nodeId);
    } else if (afterId === null) {
      newSiblings.unshift(nodeId);
    } else {
      newSiblings.push(nodeId);
    }

    node.parentId = newParentId;
    syncSiblingAfterIds(state, newParentKey);
    if (oldParentKey !== newParentKey) {
      syncSiblingAfterIds(state, oldParentKey);
    }
  },

  removeNode: (state, { nodeId }) => {
    const node = state.nodesById[nodeId];
    if (!node) return;

    // Note: recursive removal handled in action, not mutation
    // Remove from parent's children
    const parentKey = node.parentId || `root_${node.pageId}`;
    const siblings = state.childrenByParent[parentKey];
    if (siblings) {
      const index = siblings.indexOf(nodeId);
      if (index > -1) siblings.splice(index, 1);
    }

    Vue.delete(state.childrenByParent, nodeId);
    Vue.delete(state.nodesById, nodeId);
  },

  // Component registration
  registerComponent: (state, { nodeType, component }) => {
    Vue.set(state.registeredComponents, nodeType, component);
  },

  // Batching
  queueUpdate: (state, update) => {
    state.updateQueue.push(update);
  },

  clearUpdateQueue: (state) => {
    state.updateQueue = [];
  },

  setLoading: (state, loading) => {
    state.loading = loading;
  },

  setSaving: (state, saving) => {
    state.saving = saving;
  },

  setTemplates: (state, templates) => {
    state.templatesBySection = templates || {};
  },

  beginCatalogFetch: (state, { teamId, token } = {}) => {
    state.catalogToken = token;
    state.pendingCatalogTeamId = teamId;
    state.loading = true;
  },

  finishCatalogFetch: (state, { token } = {}) => {
    if (token !== undefined && token !== state.catalogToken) return;
    state.loading = false;
  },

  restoreChildrenOrder: (state, { childrenByParent }) => {
    restoreChildrenOrder(state, { childrenByParent });
  },
};

function isStaleTeamRecordWrite(state, { teamId }) {
  if (
    state.pendingCatalogTeamId != null &&
    teamId !== undefined &&
    teamId !== state.pendingCatalogTeamId
  ) {
    return true;
  }
  return (
    teamId !== undefined &&
    state.catalogTeamId != null &&
    teamId !== state.catalogTeamId
  );
}

function syncSiblingAfterIds(state, parentKey) {
  const ids = state.childrenByParent[parentKey] || [];
  ids.forEach((id, index) => {
    const node = state.nodesById[id];
    if (node) node.afterId = index === 0 ? null : ids[index - 1];
  });
}

function restoreChildrenOrder(state, { childrenByParent }) {
  Object.entries(childrenByParent || {}).forEach(([key, ids]) => {
    if (!Array.isArray(ids) || !state.childrenByParent[key]) return;
    const have = new Set(state.childrenByParent[key]);
    const next = ids.filter((id) => have.has(id));
    state.childrenByParent[key].forEach((id) => {
      if (!next.includes(id)) next.push(id);
    });
    Vue.set(state.childrenByParent, key, next);
    syncSiblingAfterIds(state, key);
  });
}

function emptyCatalog(state, teamId) {
  state.recordsById = {};
  state.treeLoaded = false;
  state.activePageId = null;
  state.pagesById = {};
  state.nodesById = {};
  state.childrenByParent = {};
  if (teamId !== undefined) {
    state.catalogTeamId = teamId;
    state.pendingCatalogTeamId = teamId;
  } else {
    state.catalogTeamId = null;
    state.pendingCatalogTeamId = null;
  }
}
